/* eslint-disable react/no-array-index-key */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useEffect, useMemo } from "react";
import { Card } from "react-bootstrap";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FormattedMessage, useIntl } from "react-intl";
import { Row } from "react-table";
import { useStateMachine } from "little-state-machine";

import "./Quotes.scss";
import DataTable from "components/organisms/DataTable/DataTable";
import GlobalTableSearch from "components/organisms/DataTable/components/GlobalTableSearch";
import PillTabs from "components/atoms/PillTabs/PillTabs";
import { useGetQuickList } from "api/queries/quick-request/useQuickList";
import Filter from "components/organisms/Filter/Filter";
import useSortedPaymentLocations from "hooks/useSortedPaymentLocations";
import ColumnFilter from "components/organisms/DataTable/components/ColumnFilter";
import { getDateParams, getObjectByKeys } from "helpers/commonHelpers";
import usePermissions from "hooks/usePermissions";
import useTableQueryRequest from "hooks/useTableQueryRequest";
import ListViewTour from "components/tours/listView/listViewTour";
import { updateShipmentIdAndTitle } from "store/store";
import useQuickFilters from "hooks/filters/useQuickFilters";
import useColumnOrderPersistor from "hooks/useColumnOrderPersistor";
import getQuickListingColumns from "./QuickColumns";

const ADDITIONAL_COLUMNS = [
  "dangerous_goods",
  "total_weight_kg",
  "total_weight_lbs",
  "total_volume_cbm",
  "expected_pickup_datetime",
  "expected_delivery_datetime",
  "export_clearance",
  "transit_clearance",
  "import_clearance",
  "service_requirement",
  "incoterms",
  "insurance",
  "custom_field_01",
  "custom_field_02",
  "reason_code",
  "cost_center",
];

function QuickQuotesListing() {
  const intl = useIntl();
  const { permissions } = usePermissions();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const paymentLocations = useSortedPaymentLocations();
  const paymentLocationsHashMap = useMemo<Record<string, any>>(
    () => getObjectByKeys(paymentLocations, "value"),
    [paymentLocations],
  );
  const { actions } = useStateMachine({ updateShipmentIdAndTitle });
  const { config: filterConfig } = useQuickFilters();
  const { persistedColumnOrder, onColumnOrderChange } = useColumnOrderPersistor({ moduleKey: "quick_request" });
  const status: string = searchParams.get("display_status") || "";

  useEffect(() => {
    if (!JSON.parse(permissions || "{}").permissions.quoting.view_quick_requests) navigate("/access-denied");
  }, []);

  const columns = useMemo(
    () =>
      getQuickListingColumns(
        intl,
        status,
        paymentLocationsHashMap,
        handleShipmentIdClick,
        JSON.parse(permissions || "{}").permissions?.quoting,
        navigate,
      ),
    [status, paymentLocationsHashMap],
  );

  const getHiddenColumns = () => {
    const hiddenColumns = localStorage.getItem("quick_requests_hidden_columns");
    if (hiddenColumns) {
      return JSON.parse(hiddenColumns);
    }
    localStorage.setItem("quick_requests_hidden_columns", JSON.stringify(ADDITIONAL_COLUMNS));
    return ADDITIONAL_COLUMNS;
  };

  const { instance, loading, totalData } = useTableQueryRequest(useGetQuickList, columns, {
    hiddenColumns: getHiddenColumns(),
    columnOrder: persistedColumnOrder,
    params: {
      display_status: status,
      ...(status !== "draft" && { properties: ADDITIONAL_COLUMNS.join(",") }),
    },
    filters: (globalFilter) => ({
      ...(globalFilter.query?.length && { query: globalFilter.query }),
      ...(globalFilter.display_status?.length && { display_status: globalFilter.display_status.join(",") }),
      ...(globalFilter.payment_location?.length && { payment_location: globalFilter.payment_location.join(",") }),
      ...(globalFilter.pickup_date?.type && getDateParams(globalFilter.pickup_date, "pickup_date")),
      ...(globalFilter.pickup_city?.length && { pickup_city: globalFilter.pickup_city }),
      ...(globalFilter.delivery_city?.length && { delivery_city: globalFilter.delivery_city }),
      ...(globalFilter.delivery_date?.type && getDateParams(globalFilter.delivery_date, "delivery_date")),
      ...(globalFilter.created_by?.length && { created_by: globalFilter.created_by }),
    }),
  });

  useEffect(() => {
    if (instance.state.globalFilter.display_status?.length) {
      searchParams.delete("display_status");
      setSearchParams(searchParams);
    }
  }, [instance.state.globalFilter.display_status]);

  const handleStatusChange = (eventKey: string | null) => {
    if (!eventKey) {
      searchParams.delete("display_status");
    } else {
      searchParams.set("display_status", eventKey || "");
    }
    setSearchParams(searchParams);
    instance.setGlobalFilter((prev: any) => ({
      ...prev,
      display_status: [],
    }));
  };

  const handleRowClick = (row: Row<any>, e: React.MouseEvent<HTMLTableRowElement, MouseEvent>) => {
    const reference = row.original?.quick_request_reference;
    const title = row.original?.quick_request_title;

    actions.updateShipmentIdAndTitle({
      shipmentId: reference,
      shipmentTitle: title,
    });

    const isDraft = status === "draft" || status === "system draft" || status === "drafts";
    if ((e.ctrlKey || e.metaKey) && !isDraft) window.open(`/shipment/${reference}/quick`, "_blank");
    else if (isDraft) {
      navigate(`/quick-request?draft_id=${reference}`);
    } else navigate(`/shipment/${reference}/quick`, { state: { data: row.original?.created_by } });
  };

  function handleShipmentIdClick(shipmentId: string, status: string) {
    const shipmentRedirectURL = status === "Shipping" ? `/shipment/${shipmentId}/tracking` : `/shipment/${shipmentId}`;
    navigate(shipmentRedirectURL);
  }

  return (
    <>
      <ListViewTour />
      <Card body className="shadow-none quote-container">
        <div className="d-flex flex-wrap align-items-center mb-4">
          <h3 className="mr-3 text-capitalize">
            <FormattedMessage id="general.quickRequests" defaultMessage="Quick requests" />
          </h3>
          <GlobalTableSearch
            currentFilters={instance.state.globalFilter}
            setFilters={instance.setGlobalFilter}
            filterKey="query"
            eventContext="Quick Request List"
          />
        </div>
        <DataTable
          tableInstance={instance}
          totalData={totalData}
          isLoading={loading}
          onRowClick={handleRowClick}
          onColumnOrderChange={onColumnOrderChange}
          headerLeft={
            <PillTabs
              id="quick-statuses"
              activeKey={status}
              onSelect={handleStatusChange}
              tabItems={[
                { eventKey: "", title: intl.formatMessage({ id: "tabs.all", defaultMessage: "All" }) },
                { eventKey: "pending", title: intl.formatMessage({ id: "tabs.pending", defaultMessage: "Pending" }) },
                {
                  eventKey: "processing",
                  title: intl.formatMessage({ id: "tabs.processing", defaultMessage: "Processing" }),
                },
                { eventKey: "shipping", title: intl.formatMessage({ id: "tabs.shipping", defaultMessage: "Shipping" }) },
                { eventKey: "draft", title: intl.formatMessage({ id: "tabs.drafts", defaultMessage: "Drafts" }) },
                { eventKey: "archived", title: intl.formatMessage({ id: "tabs.archived", defaultMessage: "Archived" }) },
              ]}
            />
          }
          headerRight={
            <div className="d-flex align-items-center">
              <Filter
                presetType="quick-request"
                eventContext="Quick Request List"
                currentFilters={instance.state.globalFilter}
                setFilters={instance.setGlobalFilter}
                config={filterConfig}
              />
              <div className="ml-3">
                <ColumnFilter
                  columns={instance.allColumns}
                  eventContext="Quick Request List"
                  listViewName="quick_requests"
                />
              </div>
            </div>
          }
          withoutBody
        />
      </Card>
    </>
  );
}

export default QuickQuotesListing;
